// Hideo - images and voice notes (video thumbnails, creator photos, thumbnails of sources and analysed videos, voice notes).
// They are not inside the profile data (they used to be Base64 in localStorage, which only has ~5 MB on phones):
// the data only holds the reference "media:<id>". The actual file lives:
// - online, in the "Hideo/Immagini" and "Hideo/Vocali" folders (uploaded with "Save online", including autosave).
//   data.media (see mediaRemote) tells the other devices where to download it;
// - on this device, as a working copy in IndexedDB (database "creatorOS_media", store "m"), to show it right away
//   without signing in to Google again (the sign-in only lasts an hour).
// The id is the fingerprint (SHA-256) of the content: the same image used twice is a single file.
// No videos: only images and voice notes.
// Ported from the legacy cloud/media.js (verbatim copy in tests/fixtures/legacy/media.js, golden tests in tests/saving*.test.ts).
import { t } from '../../i18n'
import { useDataStore } from '../../stores/data'
import { cloudOn, driveOnline, fileExt } from './common'
import { CLOUDS, CLOUD_ORDER } from './services'
import { profileData, saveAppData, writeProfileData } from '../storage/save'
import { blobToDataUrl, readImg } from '../../utils/image'
import { lsKey } from '../../data/profiles'
import { FOLDER } from '../storage/folder'

const MEDIA = { urls: {} as Record<string, string>, loading: {} as Record<string, Promise<string> | undefined>, missing: new Set<string>() }
const isMediaRef = (v: unknown): v is string => typeof v === 'string' && v.startsWith('media:')
const mediaIdOf = (ref: string) => ref.slice(6)

/** Where media files are online: media id → Google Drive file id (older saves) or { google, dropbox } */
type MediaMap = Record<string, string | Record<string, string>>
/** A record of the device copy */
interface MediaRecord { buf: ArrayBuffer; type: string; kind: 'img' | 'voice'; size: number; at: number }

// ===== Copy on the device (IndexedDB) =====
let mediaDbP: Promise<IDBDatabase> | null = null
function mediaDb(): Promise<IDBDatabase> {
  if (!mediaDbP) mediaDbP = new Promise((res, rej) => {
    if (!window.indexedDB) return rej(new Error('IndexedDB not available'))
    const r = indexedDB.open('creatorOS_media', 1)
    r.onupgradeneeded = () => r.result.createObjectStore('m')
    r.onsuccess = () => res(r.result)
    r.onerror = () => { mediaDbP = null; rej(r.error) }
  })
  return mediaDbP
}
async function mediaStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | undefined): Promise<T | undefined> {
  const db = await mediaDb()
  return new Promise((res, rej) => {
    const tx = db.transaction('m', mode), q = fn(tx.objectStore('m'))
    tx.oncomplete = () => res(q && q.result); tx.onerror = () => rej(tx.error); tx.onabort = () => rej(tx.error)
  })
}
// Record: { buf (ArrayBuffer), type, kind ('img' | 'voice'), size, at }
const mediaGet = (id: string): Promise<MediaRecord | null | undefined> => mediaStore<MediaRecord>('readonly', s => s.get(id)).catch(() => null)
const recBlob = (rec: MediaRecord) => new Blob([rec.buf], { type: rec.type })

export function dataUrlToBlob(u: string): Blob {
  const i = u.indexOf(','), head = u.slice(0, i), body = u.slice(i + 1)
  const type = (head.match(/^data:([^;,]+)/) || [])[1] || 'application/octet-stream'
  if (!/;base64/i.test(head)) return new Blob([decodeURIComponent(body)], { type })
  const bin = atob(body), a = new Uint8Array(bin.length)
  for (let k = 0; k < bin.length; k++) a[k] = bin.charCodeAt(k)
  return new Blob([a], { type })
}

export async function mediaHash(buf: ArrayBuffer): Promise<string> {
  try {
    const h = new Uint8Array(await crypto.subtle.digest('SHA-256', buf))
    return 'm_' + [...h.slice(0, 12)].map(b => b.toString(16).padStart(2, '0')).join('')
  } catch { return 'm_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8) }
}
/** Stores an image or a voice note (data URL or Blob) and returns the reference "media:<id>".
 *  If the device has no IndexedDB (rare) it returns the data URL instead */
export async function mediaPut(src: string | Blob, kind?: 'img' | 'voice'): Promise<string> {
  const blob = src instanceof Blob ? src : dataUrlToBlob(src)
  try {
    const buf = await new Response(blob).arrayBuffer(), id = await mediaHash(buf)
    kind = kind || (blob.type.startsWith('audio/') ? 'voice' : 'img')
    if (!(await mediaGet(id))) await mediaStore('readwrite', s => s.put({ buf, type: blob.type, kind, size: buf.byteLength, at: Date.now() }, id))
    if (!MEDIA.urls[id]) MEDIA.urls[id] = URL.createObjectURL(blob)
    MEDIA.missing.delete(id)
    return 'media:' + id
  } catch (e) { console.error(e); return src instanceof Blob ? blobToDataUrl(src) : src }
}

// ===== Showing an image or a voice note =====
/** Called by safeUrl (src/utils/css.ts) and the voice note player: a "media:<id>" reference becomes the URL of the copy on the
 *  device. If the copy is not ready yet the reference stays (nothing is shown) and loading starts: when it arrives,
 *  mediaPaint updates the elements already drawn */
export function mediaUrl(v: string): string {
  if (!isMediaRef(v)) return v
  const id = mediaIdOf(v)
  if (MEDIA.urls[id]) return MEDIA.urls[id]
  if (!MEDIA.missing.has(id)) void mediaLoad(id)
  return v
}
function mediaLoad(id: string, map?: MediaMap): Promise<string> {
  if (MEDIA.urls[id]) return Promise.resolve(MEDIA.urls[id])
  if (MEDIA.loading[id]) return MEDIA.loading[id]
  const p = (async () => {
    const rec = (await mediaGet(id)) || (await mediaDownload(id, map))
    if (!rec) { MEDIA.missing.add(id); return '' }
    MEDIA.missing.delete(id)
    const url = MEDIA.urls[id] || (MEDIA.urls[id] = URL.createObjectURL(recBlob(rec)))
    mediaPaint(id, url)
    return url
  })().finally(() => { delete MEDIA.loading[id] })
  return MEDIA.loading[id] = p
}
/** The elements already drawn with a "media:<id>" reference get the image or the voice note */
export function mediaPaint(id: string, url: string): void {
  const ref = 'media:' + id
  document.querySelectorAll<HTMLElement>(`[style*="${ref}"]`).forEach(el => { el.style.backgroundImage = `url("${url}")` })
  document.querySelectorAll('audio').forEach(a => { if (a.getAttribute('src') === ref) a.src = url })
}
// Where a media file is online: data.media[id] is the Google Drive file id (older saves) or
// { google, dropbox } with the file id on each service
const mediaRemote = (map: MediaMap | undefined, id: string, pid: string): string => { const v = (map || {})[id]; return !v ? '' : typeof v === 'string' ? (pid === 'google' ? v : '') : (v[pid] || '') }
function setMediaRemote(map: MediaMap, id: string, pid: string, fileId: string): void { let v = map[id]; if (typeof v === 'string') v = { google: v }; v = v || {}; v[pid] = fileId; map[id] = v }
// Download a file missing on this device from the first connected service that has it and whose sign-in is still valid
// (no sign-in window is opened here: there is no user click)
async function mediaDownload(id: string, map?: MediaMap): Promise<MediaRecord | null> {
  const raw = useDataStore().raw as { media?: MediaMap } | null
  map = map || (raw && raw.media) || {}
  if (!driveOnline) return null
  for (const pid of CLOUD_ORDER) {
    const fileId = mediaRemote(map, id, pid)
    if (!fileId || !cloudOn(pid)) continue
    try {
      if (!(await CLOUDS[pid].token(false))) continue
      const { buf, type } = await CLOUDS[pid].readBlob(fileId)
      const rec: MediaRecord = { buf, type, kind: (type || '').startsWith('audio/') ? 'voice' : 'img', size: buf.byteLength, at: Date.now() }
      await mediaStore('readwrite', s => s.put(rec, id)).catch(() => {})
      return rec
    } catch (e) { console.error(e) }
  }
  return null
}
/** After signing in: retry the missing files */
export function mediaRetry(): void { [...MEDIA.missing].forEach(id => { MEDIA.missing.delete(id); void mediaLoad(id) }) }
/** Downloads all the files of a profile right away (after "Load online", while the sign-in is valid) */
export async function mediaPrefetch(d: { media?: MediaMap }): Promise<void> {
  for (const id of mediaIdsOf(d)) { MEDIA.missing.delete(id); await mediaLoad(id, d.media) }
}

// ===== Where images and voice notes are in the data =====
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Obj = Record<string, any>
interface MediaField { o: Obj; k: string; label: string }
// The labels become file names on the online service (stored data): they stay in Italian, never translated
function mediaFields(d: Obj): MediaField[] {
  const out: MediaField[] = [], add = (o: Obj, k: string, label: string) => { if (o && o[k]) out.push({ o, k, label }) }
  ;(d.videos || []).forEach((v: Obj) => { add(v, 'thumbnail', 'Miniatura - ' + (v.title || 'video')); add(v, 'voiceNote', 'Vocale - ' + (v.title || 'video')) })
  ;(d.creators || []).forEach((c: Obj) => {
    add(c, 'avatar', 'Foto - ' + (c.name || 'creator'))
    add(c, 'banner', 'Banner - ' + (c.name || 'creator'))
    ;(c.analyzedVideos || []).forEach((a: Obj) => add(a, 'thumbnail', 'Video analizzato - ' + (a.title || 'video')))
  })
  ;(d.sources || []).forEach((s: Obj) => add(s, 'thumbnail', 'Fonte - ' + (s.title || 'fonte')))
  ;(d.savedVideos || []).forEach((v: Obj) => add(v, 'thumbnail', 'Video salvato - ' + (v.title || 'video')))
  return out
}
const mediaIdsOf = (d: Obj): string[] => [...new Set(mediaFields(d).filter(f => isMediaRef(f.o[f.k])).map(f => mediaIdOf(f.o[f.k])))]

/** Old data (or loaded files) with Base64 images and voice notes: moves them to the device copy and puts the reference
 *  instead. Very large images are scaled down (thumbnails 1280 px, creator photos 400 px, creator banners 1600 px). Returns how many were moved */
export async function mediaExtract(d: Obj): Promise<number> {
  let n = 0
  for (const f of mediaFields(d)) {
    const v = f.o[f.k]
    if (typeof v !== 'string' || !/^data:(image|audio)\//.test(v)) continue
    try {
      const src = v.startsWith('data:image/') && v.length > 400000 ? await readImg(dataUrlToBlob(v) as File, f.k === 'avatar' ? 400 : f.k === 'banner' ? 1600 : 1280) : v
      const ref = await mediaPut(src, f.k === 'voiceNote' ? 'voice' : 'img')
      if (isMediaRef(ref)) { f.o[f.k] = ref; n++ }
    } catch (e) { console.error(e) }
  }
  return n
}
/** At start-up (app.boot, src/features/app.ts): moves the Base64 data of every profile (frees browser storage) */
export async function mediaMigrateAll(): Promise<void> {
  const store = useDataStore()
  for (const p of store.profiles.list) {
    try {
      // A profile saved in this browser without any Base64 image or voice note has nothing to move: it is not read
      // (reading means parsing and normalising the whole profile, at every start)
      if (p.id !== store.profiles.current && !FOLDER.ok) {
        let raw: string | null = null
        try { raw = localStorage.getItem(lsKey(p.id)) } catch { /* storage blocked: nothing saved here */ }
        if (!raw || !/"data:(image|audio)\//.test(raw)) continue
      }
      const d = await profileData(p.id)
      if (await mediaExtract(d)) {
        if (p.id === store.profiles.current) saveAppData(d)
        else await writeProfileData(p.id, d)
      }
    } catch (e) { console.error(e) }
  }
}
/** Full copy for the file download: references go back to Base64, so the file also contains images and voice notes */
export async function mediaInline<T extends Obj>(d: T): Promise<T> {
  for (const f of mediaFields(d)) {
    const v = f.o[f.k]; if (!isMediaRef(v)) continue
    const id = mediaIdOf(v), rec = (await mediaGet(id)) || (await mediaDownload(id, d.media))
    if (rec) f.o[f.k] = await blobToDataUrl(recBlob(rec))
  }
  return d
}

// ===== Immagini and Vocali folders on the online service =====
/** Before saving the profile to a service: uploads the images and voice notes not there yet and updates data.media.
 *  Removes from data.media the ones no longer used (the online files stay). Returns true if data.media changed.
 *  File name: "<Miniatura|Vocale|Foto|…> - <title> [<media id>].<ext>" (the id finds it again, also from another device) */
export async function driveUploadMedia(d: Obj, status: (text: string) => void, pid: string): Promise<boolean> {
  const P = CLOUDS[pid], fields = mediaFields(d).filter(f => isMediaRef(f.o[f.k]))
  const used = new Set(fields.map(f => mediaIdOf(f.o[f.k])))
  if (!d.media || typeof d.media !== 'object' || Array.isArray(d.media)) d.media = {}
  const map: MediaMap = d.media, before = JSON.stringify(map)
  Object.keys(map).forEach(k => { if (!used.has(k)) delete map[k] })
  const todo = [...used].filter(id => !mediaRemote(map, id, pid))
  let i = 0
  for (const id of todo) {
    const rec = await mediaGet(id); i++; if (!rec) continue   // not on this device: the device that has it will upload it
    const progress = { service: P.name, i, n: todo.length }
    status(rec.kind === 'voice' ? t('Uploading to {service}: voice note {i} of {n}…', progress) : t('Uploading to {service}: image {i} of {n}…', progress))
    let fileId = await P.findMedia(id)
    if (!fileId) {
      const kind = rec.kind === 'voice' ? 'voice' : 'img', f = fields.find(x => mediaIdOf(x.o[x.k]) === id)
      const label = (f ? f.label : (kind === 'voice' ? 'Vocale' : 'Immagine')).replace(/[\\/:*?"<>|\[\]]+/g, ' ').trim().slice(0, 80)
      fileId = await P.uploadMedia(kind, `${label} [${id}].${fileExt(rec.type, kind)}`, recBlob(rec), id)
    }
    setMediaRemote(map, id, pid, fileId)
  }
  return JSON.stringify(map) !== before
}

// ===== Storage (Settings › Saves) =====
/** Images and voice notes of the profile: counts, bytes on this device, files not online yet / not on this device yet */
export async function mediaStats(d: Obj): Promise<{ imgs: number; voices: number; size: number; here: number; missing: number; pending: number }> {
  const fields = mediaFields(d).filter(f => isMediaRef(f.o[f.k]) || /^data:/.test(f.o[f.k]))
  const ids = mediaIdsOf(d), map: MediaMap = d.media || {}
  let size = 0, here = 0
  for (const id of ids) { const r = await mediaGet(id); if (r) { here++; size += r.size } }
  const kindOf = (f: MediaField) => f.k === 'voiceNote' ? 'voice' : 'img', uniq = (k: string) => new Set(fields.filter(f => kindOf(f) === k).map(f => f.o[f.k])).size
  // "to upload": media that are not on any service
  return { imgs: uniq('img'), voices: uniq('voice'), size, here, missing: ids.length - here, pending: ids.filter(id => !CLOUD_ORDER.some(pid => mediaRemote(map, id, pid))).length }
}

/** For tests (tests/saving*.test.ts): the state of the device copies in this session */
export const mediaSession = () => MEDIA
